// Admin-only "Launch Video" uploader — pick a video from your camera roll
// or file system and it becomes the hero video on the landing page.
// The <input type="file" accept="video/*"> pattern on mobile Safari opens
// the camera roll directly, no extra plumbing needed.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, Video, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const MAX_BYTES = 200 * 1024 * 1024; // keep frontend in sync with backend

export default function AdminLaunchVideo() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/site/launch-video");
      setState(data);
    } catch {
      setState({ has_video: false, video_url: "" });
    }
  };
  useEffect(() => { load(); }, []);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked later
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File is ${(file.size / (1024 * 1024)).toFixed(0)}MB — max is ${MAX_BYTES / (1024 * 1024)}MB. Try compressing it first.`);
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/launch-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      toast.success("Launch video uploaded — refresh the landing page to see it.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); setProgress(0); }
  };

  const clear = async () => {
    if (!window.confirm("Remove the current launch video? The hero will show the placeholder again.")) return;
    try {
      await api.delete("/admin/launch-video");
      toast.success("Video cleared.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to clear");
    }
  };

  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const streamUrl = state?.has_video ? `${backendUrl}${state.video_url}` : "";

  return (
    <div className="mx-auto max-w-3xl px-6 py-14" data-testid="admin-launch-video">
      <Link to="/admin" className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100">
        <ArrowLeft size={14} /> Back to admin
      </Link>
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-1 rounded-full mt-4"
            style={{ background: "rgba(107,47,186,0.14)", color: "var(--avt-purple)" }}>
        <Video className="h-3 w-3" /> Launch video
      </div>
      <h1 className="font-serif text-4xl mt-3">Upload your launch reel</h1>
      <p className="mt-2 opacity-70">
        Pick a video from your phone — we&rsquo;ll host it and swap it into the
        &ldquo;Watch the demo&rdquo; button on the landing page. Max 200MB,
        60–90 seconds works best. MP4 or MOV.
      </p>

      <div className="mt-10 space-y-8">
        {state?.has_video ? (
          <div className="rounded-sm p-6 border" style={{ borderColor: "rgba(0,0,0,0.08)" }}
                data-testid="admin-launch-video-preview">
            <div className="overline mb-3" style={{ color: "var(--moss)" }}>Currently live</div>
            <video
              key={streamUrl}
              src={streamUrl}
              controls
              playsInline
              className="w-full rounded-sm bg-black"
              style={{ maxHeight: 480 }}
              data-testid="admin-launch-video-player"
            />
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap text-xs opacity-70">
              <div>
                {state.original_filename ? `${state.original_filename} · ` : ""}
                {(state.size_bytes / (1024 * 1024)).toFixed(1)} MB · uploaded {new Date(state.uploaded_at).toLocaleString()}
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={pick}
                disabled={busy}
                className="btn-primary rounded-full inline-flex items-center gap-2"
                data-testid="admin-launch-video-replace-btn"
              >
                <Upload size={14} /> Replace video
              </Button>
              <Button
                onClick={clear}
                disabled={busy}
                variant="outline"
                className="rounded-full inline-flex items-center gap-2"
                data-testid="admin-launch-video-clear-btn"
                style={{ color: "var(--terracotta)" }}
              >
                <Trash2 size={14} /> Remove
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="rounded-sm p-10 border-2 border-dashed text-center"
            style={{ borderColor: "rgba(107,47,186,0.4)" }}
            data-testid="admin-launch-video-empty"
          >
            <Video className="mx-auto h-10 w-10 opacity-30" />
            <h3 className="mt-4 font-serif text-2xl">No launch video yet</h3>
            <p className="mt-2 text-sm opacity-70">
              The hero currently shows the &ldquo;Watch the demo · coming soon&rdquo; placeholder.
            </p>
            <Button
              onClick={pick}
              disabled={busy}
              className="btn-primary rounded-full inline-flex items-center gap-2 mt-6"
              data-testid="admin-launch-video-upload-btn"
            >
              <Upload size={14} /> Pick a video
            </Button>
          </div>
        )}

        {busy && (
          <div className="rounded-sm p-4 border" style={{ borderColor: "rgba(107,47,186,0.3)", background: "rgba(107,47,186,0.06)" }}>
            <div className="inline-flex items-center gap-2 text-sm mb-2 font-bold" style={{ color: "var(--avt-purple)" }}>
              <Loader2 className="h-4 w-4 animate-spin" /> Uploading… {progress}%
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(107,47,186,0.15)" }}>
              <div
                className="h-full transition-all"
                style={{ width: `${progress}%`, background: "var(--avt-purple)" }}
              />
            </div>
            <p className="mt-2 text-xs opacity-60">Big files can take a minute — don&rsquo;t close this tab.</p>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm,video/*"
          onChange={onFile}
          className="hidden"
          data-testid="admin-launch-video-input"
        />

        <div className="text-xs opacity-60 leading-relaxed">
          <p className="font-bold uppercase tracking-widest opacity-80 mb-1">Tips</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Filming vertically? Try to keep the important content centered — the hero renders 16:9.</li>
            <li>200MB cap. If your file is bigger, iOS Photos → Share → Save Video → "Small" size usually gets you under.</li>
            <li>The video autoplays muted in a lightbox when a visitor taps "Watch the demo" on the hero.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
